import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getDatabase, provideDatabase } from '@angular/fire/database';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetModule,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { IntercomModule } from 'ng-intercom';
import { NgxFileDropModule } from 'ngx-file-drop';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { SafePipeModule } from 'safe-pipe';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { MatTableModule } from '@angular/material/table';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorIntl } from '@angular/material/paginator';
import {
  Chart,
  ArcElement,
  CategoryScale,
  DoughnutController,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { MatSidenavModule } from '@angular/material/sidenav';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthInterceptor } from './auth.interceptor';
import { BillingActivate } from './billing.guard';
import { CommonComponentsModule } from './common/common-components.module';
import { ZoomUtil } from './common/utils/zoom.util';
import { AddIframeComponent } from './dialogs/add-iframe/add-iframe.component';
import { AddWebViewerComponent } from './dialogs/add-web-viewer/add-web-viewer.component';
import { ChangeLanguageConfirmationDialogComponent } from './dialogs/change-language-confirmation-dialog/change-language-confirmation-dialog.component';
import { CloseFrameComponent } from './dialogs/close-frame/close-frame.component';
import { CommonDialogComponent } from './dialogs/common-dialog/common-dialog.component';
import { DeleteSpaceCommentComponent } from './dialogs/delete-space-comment/delete-space-comment.component';
import { DuplicateSpaceComponent } from './dialogs/duplicate-space/duplicate-space.component';
import { EmbeddedGoogleLoginComponent } from './dialogs/embedded-google-login/embedded-google-login.component';
import { LinkLoginComponent } from './dialogs/link-login/link-login.component';
import { LogoutDialogComponent } from './dialogs/logout/logout.component';
import { UploadDialogComponent as MediaUploadDialogComponent } from './dialogs/media-upload/upload-dialog.component';
import { MoveToFolderComponent } from './dialogs/move-to-folder/move-to-folder.component';
import { NotSupportedBrowsersComponent } from './dialogs/not-supported-browsers/not-supported-browsers.component';
import { PhetEmbedDialogComponent } from './dialogs/phet-embed/phet-embed.component';
import { RenameSpaceComponent } from './dialogs/rename-space/rename-space.component';
import { RevokedInfoDialogComponent } from './dialogs/revoked-info-dialog/revoke-info-dialog.component';
import { SelectAllComponent } from './dialogs/select-all/select-all.component';
import { SpeedbumpModalComponent } from './dialogs/speedbump-modal/speedbump-modal.component';
import { VirtualBackgroundPanelComponent } from './dialogs/virtual-background-panel/virtual-background-panel.component';
import { VisibilityConfirmDialogComponent } from './dialogs/visibility-confirm/visibility-confirm.component';
import { VisibilityDraftAlertDialogComponent } from './dialogs/visibility-draft-alert/visibility-draft-alert.component';
import { DirectivesModule } from './directives/directives.module';
import { ErrorInterceptor } from './error.interceptor';
import { LoginActivate } from './login.guard';
import { AppMaterialModule } from './modules/app-material/app-material.module';
import { FeatureFlagsComponent } from './nav-bar/feature-flags/feature-flags.component';
import { ImpersonateDialogComponent, NavBarComponent } from './nav-bar/nav-bar.component';
import { NavSidebarItemComponent } from './nav-bar/nav-sidebar-item/nav-sidebar-item.component';
import { NavSidebarSectionComponent } from './nav-bar/nav-sidebar-section/nav-sidebar-section.component';
import { NavSidebarUpgradeBannerComponent } from './nav-bar/nav-sidebar-upgrade-banner/nav-sidebar-upgrade-banner.component';
import { NavSidebarCollapseButtonComponent } from './nav-bar/nav-sidebar-collapse-button/nav-sidebar-collapse-button.component';
import { NavSpacesSwitcherComponent } from './nav-bar/nav-spaces-switcher/nav-spaces-switcher.component';
import { TrialBannerComponent } from './settings/user-settings-panel/user-settings-payment/trial-banner/trial-banner.component';
import { PipeModule } from './pipes/pipes.module';
import { UpgradeVersionNotificationComponent } from './popup-notifications/upgrade-version-notification/upgrade-version-notification.component';
import { SentryErrorHandler } from './sentry-error-handler';
import { EmbedVideoService } from './services/embed-video.service';
import { SpaceBoardsService } from './services/space-boards.service';
import { SessionActivate } from './session.guard';
import { UserSettingsAccountComponent } from './settings/user-settings-panel/user-settings-account/user-settings-account.component';
import { UserSettingsLanguageComponent } from './settings/user-settings-panel/user-settings-language/user-settings-language.component';
import { UserSettingsPanelComponent } from './settings/user-settings-panel/user-settings-panel.component';
import { UserSettingsPaymentComponent } from './settings/user-settings-panel/user-settings-payment/user-settings-payment.component';
import { UserSettingsPermissionsComponent } from './settings/user-settings-panel/user-settings-permissions/user-settings-permissions.component';
import { UserSettingsAiAssistComponent } from './settings/user-settings-panel/user-settings-ai-assist/user-settings-ai-assist.component';
import { UserSettingsBrandingComponent } from './settings/user-settings-panel/user-settings-branding/user-settings-branding.component';
import { SpaceRepository } from './state/space.repository';
import { ToolbarStateRepository } from './state/toolbar-state.repository';
import { UIPropsRepository } from './state/ui-props.repository';
import { SwitcherComponent } from './switcher/switcher.component';
import { TopBarComponent } from './ui/top-bar/top-bar.component';
import { UiModule } from './ui/ui.module';
import { UserActivate } from './user.guard';
import { ObservableUtils } from './utilities/ObservableUtils';
import { ObjectDuplicationUtilsService } from './utilities/object-duplication-utils.service';
import { TelemetryService } from './services/telemetry.service';
import { UserSettingsPermissionsToggleComponent } from './settings/user-settings-panel/user-settings-permissions/user-settings-permissions-toggle/user-settings-permissions-toggle.component';
import { UserSettingsPermissionsSelectComponent } from './settings/user-settings-panel/user-settings-permissions/user-settings-permissions-select/user-settings-permissions-select.component';
import { SelectSpaceDialogComponent } from './dialogs/select-space-dialog/select-space-dialog.component';
import { CreateSpaceDialogComponent } from './dialogs/create-space-dialog/create-space-dialog.component';
import { NetworkCacheService } from './services/network-cache.service';
import { TooltipDirective } from './standalones/directives/pncl-tooltip.directive';
import { UserSettingsAdvancedComponent } from './settings/user-settings-panel/user-settings-advanced/user-settings-advanced.component';
import { AddPencilAiComponent } from './dialogs/add-pencil-ai/add-pencil-ai.component';
import { PencilButtonComponent } from './standalones/components/pencil-button/pencil-button.component';
import {
  PencilIconComponent,
  PENCIL_LUCIDE_ICONS_PROVIDER,
} from './standalones/components/pencil-icon/pencil-icon.component';
import { PencilMenuItemComponent } from './standalones/components/pencil-menu-item/pencil-menu-item.component';
import { AvailableEventHostsDialogComponent } from './dialogs/available-event-hosts-dialog/available-event-hosts-dialog.component';
import { UploadDialogComponent } from './dialogs/upload/upload-dialog.component';
import { UploadModule } from './upload/upload.module';
import { ChooseSpaceTemplateDialogComponent } from './dialogs/choose-space-template-dialog/choose-space-template-dialog.component';
import { UserSettingsSitesManagementComponent } from './settings/user-settings-panel/user-settings-sites-management/user-settings-sites-management.component';
import { UserSettingsInstitutionCustomAttributesComponent } from './settings/user-settings-panel/user-settings-institution-custom-attributes/user-settings-institution-custom-attributes.component';
import { PencilPillComponent } from './standalones/components/pencil-pill/pencil-pill.component';
import { UserSettingsBillableUsageComponent } from './settings/user-settings-panel/user-settings-billable-usage/user-settings-billable-usage.component';
import { CustomExpansionPanelComponent } from './standalones/components/custom-expansion-panel/custom-expansion-panel.component';
import { CapabilityTierService } from './services/capability-tier.service';
import { PlanAndUsageComponent } from './settings/user-settings-panel/user-settings-billable-usage/plan-and-usage/plan-and-usage.component';
import { BillingAnalyticsAndInvoicesComponent } from './settings/user-settings-panel/user-settings-billable-usage/billing-analytics-and-invoices/billing-analytics-and-invoices.component';
import { DateRangePickerComponent } from './standalones/components/date-range-picker/date-range-picker.component';
import { DateRangePickerDirective } from './standalones/directives/date-range-picker.directive';
import { SettingsTabVisibleDirective } from './standalones/directives/settings-tab-visible.directive';
import { UserSettingsFeedbackComponent } from './settings/user-settings-panel/user-settings-feedback/user-settings-feedback.component';
import { UserSettingsIntegrationsComponent } from './settings/user-settings-panel/user-settings-integrations/user-settings-integrations.component';
import { UserSettingsIntegrationsApiComponent } from './settings/user-settings-panel/user-settings-integrations/user-settings-integrations-api/user-settings-integrations-api.component';
import { UserSettingsEventsComponent } from './settings/user-settings-panel/user-settings-events/user-settings-events.component';
import { UserSettingsSessionsComponent } from './settings/user-settings-panel/user-settings-sessions/user-settings-sessions.component';
import { UserSettingsAlertsComponent } from './settings/user-settings-panel/user-settings-alerts/user-settings-alerts.component';
import { AlertPriorityDropdownComponent } from './alerts/alert-priority-dropdown/alert-priority-dropdown.component';
import { FeatureManagementService } from './services/feature-management.service';
import { PencilSpinnerComponent } from './ui/pencil-spinner/pencil-spinner.component';
import { ModalTemplateComponent } from './standalones/components/modals/modal-template/modal-template.component';
import { CustomMatPaginatorIntlService } from './services/custom-mat-paginator-intl.service';
import { DevPanelComponent } from './ui/dev-panel/dev-panel.component';
import { UserSettingsIntegrationsWebhooksComponent } from './settings/user-settings-panel/user-settings-integrations/user-settings-integrations-webhooks/user-settings-integrations-webhooks.component';
import { UserSettingsIntegrationsEdlinkComponent } from './settings/user-settings-panel/user-settings-integrations/user-settings-integrations-edlink/user-settings-integrations-edlink.component';
import { PushNotificationsService } from './services/push-notifications.service';
import { UserSettingsLtiComponent } from './settings/user-settings-panel/user-settings-lti/user-settings-lti.component';
import { CustomHelpRedirectionDirective } from './standalones/directives/custom-help-redirection.directive';
import { PencilTimezonePickerComponent } from './standalones/components/pencil-timezone-picker/pencil-timezone-picker.component';
import { SelectionModalComponent } from './standalones/components/modals/selection-modal/selection-modal.component';
import { SingleSelectionListModalComponent } from './standalones/components/modals/selection-modal/single-selection-list-modal/single-selection-list-modal.component';
import { MultiSelectionListModalComponent } from './standalones/components/modals/selection-modal/multi-selection-list-modal/multi-selection-list-modal.component';
import { SelectionListModalComponent } from './standalones/components/modals/selection-modal/selection-list-modal/selection-list-modal.component';
import { ConfirmationModalComponent } from './standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { RadioGroupInputComponent } from './standalones/components/radio-group-input/radio-group-input.component';
import { RecordingAccessMenuComponent } from './standalones/components/recording-access-menu/recording-access-menu.component';
import { UserSettingsPermissionsDropdownComponent } from './settings/user-settings-panel/user-settings-permissions/user-settings-permissions-dropdown/user-settings-permissions-dropdown.component';
import { UserSettingsWarningComponent } from './settings/user-settings-panel/user-settings-permissions/user-settings-warning.component';
import { SelectUsersDialogComponent } from './dialogs/select-users-dialog/select-users-dialog.component';
import { PlanCardComponent } from './settings/user-settings-panel/user-settings-payment/plan-card/plan-card.component';
import { PlanListComponent } from './settings/user-settings-panel/user-settings-payment/plan-list/plan-list.component';
import { ReassignUserDialogComponent } from './dialogs/reassign-user-dialog/reassign-user-dialog.component';
import { PresenceIndicatorUsersComponent } from './sessions/spaces-view/presence-indicator-users/presence-indicator-users.component';
import { VisibilityMenuComponent } from './standalones/components/visibility-menu/visibility-menu.component';
import { LoadingV2Component } from './standalones/components/loading-v2/loading-v2.component';

Chart.register(
  ArcElement,
  DoughnutController,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Legend,
  Tooltip,
);
// Chart.js defaults to Helvetica/Arial, which has no 600 weight and renders semibold text as bold.
Chart.defaults.font.family = "'Source Sans Pro', sans-serif";

export function telemetryInitializer(telemetry: TelemetryService) {
  return () => {
    telemetry.init();
    telemetry.toggleDOMTracking(true); // start capturing DOM on app start
  };
}

export function networkCacheInitializer(networkCache: NetworkCacheService) {
  return async () => {
    // Clear all cached values on start
    await networkCache.clearAllCaches();
  };
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const isLocalHost = (hostname = window.location.hostname) =>
  ['localhost', '127.0.0.1', '', '::1'].includes(hostname);

@NgModule({
  declarations: [
    AppComponent,
    NavBarComponent,
    LogoutDialogComponent,
    SelectAllComponent,
    ImpersonateDialogComponent,
    VisibilityConfirmDialogComponent,
    VisibilityDraftAlertDialogComponent,
    FeatureFlagsComponent,
    UploadDialogComponent,
    MediaUploadDialogComponent,
    CommonDialogComponent,
    PhetEmbedDialogComponent,
    MoveToFolderComponent,
    LinkLoginComponent,
    NotSupportedBrowsersComponent,
    CloseFrameComponent,
    RevokedInfoDialogComponent,
    UpgradeVersionNotificationComponent,
    RenameSpaceComponent,
    AddIframeComponent,
    AddWebViewerComponent,
    VirtualBackgroundPanelComponent,
    ChangeLanguageConfirmationDialogComponent,
    EmbeddedGoogleLoginComponent,
    SwitcherComponent,
    DeleteSpaceCommentComponent,
    DuplicateSpaceComponent,
    UserSettingsPanelComponent,
    UserSettingsAccountComponent,
    UserSettingsLanguageComponent,
    UserSettingsAdvancedComponent,
    UserSettingsPaymentComponent,
    PlanListComponent,
    PlanCardComponent,
    UserSettingsAiAssistComponent,
    UserSettingsBillableUsageComponent,
    UserSettingsEventsComponent,
    PlanAndUsageComponent,
    BillingAnalyticsAndInvoicesComponent,
    UserSettingsPermissionsComponent,
    UserSettingsPermissionsToggleComponent,
    UserSettingsPermissionsSelectComponent,
    UserSettingsSitesManagementComponent,
    UserSettingsInstitutionCustomAttributesComponent,
    UserSettingsFeedbackComponent,
    UserSettingsSessionsComponent,
    UserSettingsAlertsComponent,
    SelectSpaceDialogComponent,
    CreateSpaceDialogComponent,
    AddPencilAiComponent,
    AvailableEventHostsDialogComponent,
    ChooseSpaceTemplateDialogComponent,
    UserSettingsIntegrationsComponent,
    UserSettingsIntegrationsApiComponent,
    UserSettingsBrandingComponent,
    UserSettingsIntegrationsWebhooksComponent,
    UserSettingsIntegrationsEdlinkComponent,
    UserSettingsLtiComponent,
    SelectUsersDialogComponent,
    ReassignUserDialogComponent,
  ],
  providers: [
    PENCIL_LUCIDE_ICONS_PROVIDER,
    {
      provide: APP_INITIALIZER,
      useFactory: telemetryInitializer,
      deps: [TelemetryService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: networkCacheInitializer,
      deps: [NetworkCacheService],
      multi: true,
    },
    LoginActivate,
    UserActivate,
    BillingActivate,
    SessionActivate,
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    EmbedVideoService,
    ObservableUtils,
    ZoomUtil,
    ObjectDuplicationUtilsService,
    SpaceRepository,
    SpaceBoardsService,
    CapabilityTierService,
    FeatureManagementService,
    UIPropsRepository,
    ToolbarStateRepository,
    PushNotificationsService,
    {
      provide: MatDialogRef,
      useValue: {},
    },
    {
      provide: MatBottomSheetRef,
      useValue: {},
    },
    {
      provide: MAT_BOTTOM_SHEET_DATA,
      useValue: {},
    },
    {
      provide: MAT_DIALOG_DATA,
      useValue: {},
    },
    {
      provide: MatPaginatorIntl,
      useClass: CustomMatPaginatorIntlService,
    },
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    ServiceWorkerModule.register('./notification-service-worker.js', {
      enabled: !isLocalHost() || environment.runServiceWorkerLocally,
      // Register immediately to ensure SW updates are applied even in embedded contexts
      registrationStrategy: 'registerImmediately',
    }),
    ReactiveFormsModule,
    FormsModule,
    AppMaterialModule,
    provideAuth(() => {
      const auth = getAuth();
      return auth;
    }),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideDatabase(() => {
      const database = getDatabase();
      return database;
    }),
    provideStorage(() => {
      const storage = getStorage();
      return storage;
    }),
    DragDropModule,
    PipeModule,
    SafePipeModule,
    UiModule,
    TopBarComponent,
    CommonComponentsModule,
    VisibilityMenuComponent,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    IntercomModule.forRoot({
      appId: 'keleytc7',
      updateOnRouterChange: true,
    }),
    GoogleTagManagerModule.forRoot({
      id: environment.gtmId,
    }),
    NgSelectModule,
    NgxMatIntlTelInputComponent,
    NgxFileDropModule,
    DirectivesModule,
    InfiniteScrollModule,
    PresenceIndicatorUsersComponent,
    OverlayModule,
    MatProgressBarModule,
    MatBottomSheetModule,
    MatDialogModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatSelectModule,
    MatTabsModule,
    MatCheckboxModule,
    NgbTooltipModule,
    TooltipDirective,
    AlertPriorityDropdownComponent,
    SettingsTabVisibleDirective,
    MatTableModule,
    MatNativeDateModule,
    PencilSpinnerComponent,
    PencilButtonComponent,
    PencilIconComponent,
    PencilMenuItemComponent,
    MatIconModule,
    NavSidebarItemComponent,
    NavSidebarSectionComponent,
    NavSidebarUpgradeBannerComponent,
    NavSidebarCollapseButtonComponent,
    NavSpacesSwitcherComponent,
    TrialBannerComponent,
    UploadModule,
    PencilPillComponent,
    CustomExpansionPanelComponent,
    DateRangePickerComponent,
    DateRangePickerDirective,
    ModalTemplateComponent,
    DevPanelComponent,
    CustomHelpRedirectionDirective,
    PencilTimezonePickerComponent,
    SelectionModalComponent,
    SingleSelectionListModalComponent,
    MultiSelectionListModalComponent,
    SelectionListModalComponent,
    ConfirmationModalComponent,
    RecordingAccessMenuComponent,
    UserSettingsPermissionsDropdownComponent,
    UserSettingsWarningComponent,
    SpeedbumpModalComponent,
    RadioGroupInputComponent,
    LoadingV2Component,
  ],
})
export class AppModule {}
